/**
 * Iter 247 — Page d'audit des modifications de permissions.
 *
 * Lecture seule. Affiche :
 * - 4 KPIs : Total, 24h, 7j, Top actor
 * - Filtre par target_user_id, actor_user_id, days (7/30/90)
 * - Tableau des changements avec diff visuel (vert pour +, rouge pour -)
 *
 * Visible pour : superadmin / admin (cross-tenant) / tenant_admin (scopé à son tenant)
 */
import { useEffect, useState, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import {
  History, Loader2, ArrowRight, ChevronDown, Search, Shield,
  TrendingUp, Clock, User, RefreshCw,
} from "lucide-react";

const SCOPE_LABEL = {
  staff: { label: "Staff Blitzz", color: "#a855f7" },
  tenant_member: { label: "Tenant Member", color: "#10b981" },
};

export default function PermissionsAuditPage() {
  const { axiosAuth, user: currentUser } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);

  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get(`/admin/audit/permissions?days=${days}&limit=200`),
        api.get(`/admin/audit/permissions/stats`),
      ]);
      setItems(listRes.data.items || []);
      setStats(statsRes.data || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(it =>
      (it.target_email || "").toLowerCase().includes(q)
      || (it.actor_email || "").toLowerCase().includes(q)
      || (it.preset_applied || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto space-y-5" data-testid="permissions-audit-page">
      <PageHeader
        icon={<History />}
        title="Audit des permissions"
        description="Journal des modifications d'autorisations (Staff Blitzz + Tenant Members)."
        actions={
          <Button onClick={load} variant="ghost" size="sm" className="text-slate-400" data-testid="btn-refresh-audit">
            <RefreshCw className="w-4 h-4 mr-2" /> Rafraîchir
          </Button>
        }
      />

      {/* KPIs */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi icon={History} label="Total" value={stats.total} color="#3b82f6" />
          <Kpi icon={Clock} label="24 dernières heures" value={stats.last_24h} color="#f59e0b" />
          <Kpi icon={TrendingUp} label="7 derniers jours" value={stats.last_7d} color="#10b981" />
          <Kpi icon={User} label="Top admin" value={stats.top_actors?.[0]?.actor_email?.split("@")[0] || "—"} color="#a855f7" />
        </div>
      )}

      {/* Filters */}
      <div className="bg-[#14101c] border border-white/5 rounded-xl p-4 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Période :</span>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="bg-[#0b0811] border border-white/10 rounded px-2 py-1 text-xs text-white"
            data-testid="filter-days"
          >
            <option value={7}>7 jours</option>
            <option value={30}>30 jours</option>
            <option value={90}>90 jours</option>
            <option value={365}>1 an</option>
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search className="w-3.5 h-3.5 text-slate-500" />
          <input
            type="text"
            placeholder="Rechercher par email (target ou actor)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="flex-1 bg-[#0b0811] border border-white/10 rounded px-2 py-1.5 text-sm text-white"
            data-testid="filter-search"
          />
        </div>
        <span className="text-xs text-slate-500">{filtered.length} entrée(s)</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#f7c935]" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-[#14101c] border border-white/5 rounded-xl p-12 text-center text-slate-500">
          Aucune modification de permission sur cette période.
        </div>
      ) : (
        <div className="bg-[#14101c] border border-white/5 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-[#0b0811] text-[10px] uppercase text-slate-400">
              <tr>
                <th className="text-left p-3">Date</th>
                <th className="text-left p-3">Acteur</th>
                <th className="text-left p-3">Cible</th>
                <th className="text-left p-3">Scope</th>
                <th className="text-left p-3">Changements</th>
                <th className="text-left p-3">Preset</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((row) => (
                <AuditRow key={row.audit_id} row={row} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Kpi({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: `${color}20`, color }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      <div className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">{label}</div>
    </div>
  );
}

function AuditRow({ row }) {
  const [expanded, setExpanded] = useState(false);
  const ts = new Date(row.timestamp);
  const scope = SCOPE_LABEL[row.scope] || { label: row.scope, color: "#94a3b8" };
  const changes = row.changes || {};
  const changesEntries = Object.entries(changes);
  return (
    <>
      <tr className="border-t border-white/5 hover:bg-white/[0.02] cursor-pointer" onClick={() => setExpanded(!expanded)} data-testid={`audit-row-${row.audit_id}`}>
        <td className="p-3 text-xs">
          <div className="text-white">{ts.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" })}</div>
          <div className="text-slate-500">{ts.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</div>
        </td>
        <td className="p-3">
          <div className="text-sm text-white">{row.actor_email}</div>
          <div className="text-[10px] text-slate-500">{row.actor_role}</div>
        </td>
        <td className="p-3">
          <div className="text-sm text-white">{row.target_email}</div>
          <div className="text-[10px] text-slate-500">{row.target_role}</div>
        </td>
        <td className="p-3">
          <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: `${scope.color}20`, color: scope.color }}>
            {scope.label}
          </span>
        </td>
        <td className="p-3">
          <div className="flex items-center gap-1 text-xs">
            <span className="font-bold text-white">{row.changes_count}</span>
            <span className="text-slate-500">perm(s)</span>
            <ChevronDown className={`w-3 h-3 text-slate-500 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </div>
        </td>
        <td className="p-3">
          {row.preset_applied ? (
            <span className="text-xs px-2 py-0.5 rounded bg-[#f7c935]/15 text-[#f7c935] font-bold">{row.preset_applied}</span>
          ) : (
            <span className="text-xs text-slate-600">—</span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={6} className="bg-black/40 p-4">
            <div className="text-[11px] uppercase text-slate-500 mb-2">Diff des permissions</div>
            <div className="space-y-1">
              {changesEntries.length === 0 ? (
                <span className="text-xs text-slate-500">Aucun changement détaillé (preset seul appliqué)</span>
              ) : changesEntries.map(([key, diff]) => (
                <div key={key} className="flex items-center gap-2 text-xs">
                  <span className={`px-1.5 py-0.5 rounded font-mono ${diff.old ? "bg-red-500/15 text-red-400 line-through" : "bg-white/5 text-slate-500"}`}>
                    {String(diff.old)}
                  </span>
                  <ArrowRight className="w-3 h-3 text-slate-600" />
                  <span className={`px-1.5 py-0.5 rounded font-mono ${diff.new ? "bg-emerald-500/15 text-emerald-400" : "bg-white/5 text-slate-500"}`}>
                    {String(diff.new)}
                  </span>
                  <span className="text-slate-300 font-mono">{key}</span>
                </div>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
